import { useState } from "react";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle, 
  Send,
  CheckCircle2,
  Shield,
  Zap,
  Users,
  ArrowRight,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    workType: "",
    location: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Veuillez remplir au minimum votre nom et téléphone");
      return;
    }

    setIsSubmitting(true);

    try {
      // Formspree endpoint
      const response = await fetch("https://formspree.io/f/mvzwyeky", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || "Non renseigné",
          workType: formData.workType || "Non précisé",
          location: formData.location || "Non précisée",
          message: formData.message || "Pas de message",
          _subject: `Nouvelle demande de contact - ${formData.name}`,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Message envoyé avec succès !");
        setFormData({ name: "", email: "", phone: "", workType: "", location: "", message: "" });
      } else {
        throw new Error("Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer ou nous contacter par téléphone.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const workTypes = [
    "Rénovation intérieure complète",
    "Plafonnage",
    "Carrelage",
    "Peinture intérieure",
    "Rénovation salle de bain",
    "Pose de sol / Parquet",
    "Autre"
  ];

  const locations = [
    "Bruxelles-Ville",
    "Uccle",
    "Ixelles",
    "Waterloo",
    "Rhode-Saint-Genèse",
    "Sint-Pieters-Leeuw",
    "Woluwe-Saint-Pierre",
    "Woluwe-Saint-Lambert",
    "Forest",
    "Anderlecht",
    "Schaerbeek",
    "Etterbeek",
    "Auderghem",
    "Autre commune"
  ];

  const reassuranceItems = [
    {
      icon: Zap,
      title: "Réponse rapide",
      description: "Nous vous rappelons sous 24h"
    },
    {
      icon: Shield,
      title: "Devis 100% gratuit",
      description: "Sans engagement de votre part"
    },
    {
      icon: MapPin,
      title: "Bruxelles & environs",
      description: "Intervention dans toute la région"
    },
    {
      icon: Users,
      title: "15+ ans d'expérience",
      description: "Équipe qualifiée et professionnelle"
    }
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section - Phone Number Prominent */}
      <section className="bg-[#0A0A0A] py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#B87333]/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              Contactez-nous
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold text-white mb-6">
              Parlons de votre <span className="text-copper-gradient">projet</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Une question ? Un projet de rénovation ? Contactez-nous directement, nous vous répondons rapidement.
            </p>
          </div>

          {/* Big Phone Number CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a
              href="tel:+32488467522"
              className="group flex items-center gap-4 bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white px-8 py-5 rounded-2xl shadow-2xl transition-all transform hover:scale-105"
            >
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                <Phone size={28} className="group-hover:animate-pulse" />
              </div>
              <div className="text-left">
                <p className="text-sm opacity-80">Appelez-nous maintenant</p>
                <p className="text-2xl sm:text-3xl font-bold">+32 488 46 75 22</p>
              </div>
            </a>
            
            <a
              href={`https://wa.me/32488467522?text=${encodeURIComponent("Bonjour Perfect Team Renov 👋\n\nJe souhaite discuter d'un projet de rénovation.\n\nMerci de me recontacter !")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-5 rounded-2xl shadow-xl transition-all transform hover:scale-105"
            >
              <MessageCircle size={28} />
              <div className="text-left">
                <p className="text-sm opacity-80">Écrivez-nous sur</p>
                <p className="text-xl font-bold">WhatsApp</p>
              </div>
            </a>
          </div>

          {/* Reassurance Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {reassuranceItems.map((item, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center"
              >
                <item.icon className="text-[#B87333] mx-auto mb-2" size={28} />
                <h3 className="text-white font-semibold text-sm">{item.title}</h3>
                <p className="text-white/60 text-xs mt-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10">
              <div className="mb-8">
                <h2 className="text-2xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-2">
                  Demande de contact rapide
                </h2>
                <p className="text-[#6B6B6B]">
                  Remplissez ce formulaire et nous vous recontactons sous 24h par téléphone ou email.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {isSubmitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="text-green-600" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">Message envoyé !</h3>
                    <p className="text-[#6B6B6B] mb-6">
                      Nous avons bien reçu votre demande et vous recontacterons sous 24h.
                    </p>
                    <Button
                      type="button"
                      onClick={() => setIsSubmitted(false)}
                      variant="outline"
                      className="border-[#B87333] text-[#B87333] hover:bg-[#B87333] hover:text-white"
                    >
                      Envoyer un autre message
                    </Button>
                  </div>
                ) : (
                  <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Téléphone <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+32 XXX XX XX XX"
                      className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Email
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Type de travaux
                    </label>
                    <Select onValueChange={(value) => handleSelectChange("workType", value)}>
                      <SelectTrigger className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        {workTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                      Localisation
                    </label>
                    <Select onValueChange={(value) => handleSelectChange("location", value)}>
                      <SelectTrigger className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]">
                        <SelectValue placeholder="Votre commune" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc} value={loc}>
                            {loc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    Décrivez votre projet (optionnel)
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Détails sur votre projet : surface, état actuel, souhaits particuliers..."
                    rows={4}
                    className="border-gray-200 focus:border-[#B87333] focus:ring-[#B87333]"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="submit-contact-btn"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white py-6 rounded-xl text-lg font-semibold disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Envoyer ma demande
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-[#6B6B6B]">
                  <CheckCircle2 className="inline mr-1 text-green-500" size={16} />
                  Réponse garantie sous 24h
                </p>
                  </>
                )}
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-6">
              {/* Quick Contact Cards */}
              <div className="bg-[#0A0A0A] rounded-3xl p-8 text-white">
                <h3 className="text-xl font-['Playfair_Display'] font-bold mb-6">
                  Contact direct
                </h3>
                
                <div className="space-y-4">
                  <a 
                    href="tel:+32488467522"
                    className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#B87333] rounded-full flex items-center justify-center">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Téléphone</p>
                      <p className="text-lg font-semibold">+32 488 46 75 22</p>
                    </div>
                  </a>
                  
                  <a 
                    href="mailto:info@perfectteamrenov.be"
                    className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#B87333]/20 rounded-full flex items-center justify-center">
                      <Mail size={24} className="text-[#B87333]" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Email</p>
                      <p className="text-lg font-semibold">info@perfectteamrenov.be</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                    <div className="w-12 h-12 bg-[#B87333]/20 rounded-full flex items-center justify-center">
                      <MapPin size={24} className="text-[#B87333]" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Adresse</p>
                      <p className="font-semibold">Bergensesteenweg 24/8</p>
                      <p className="text-white/80">1600 Sint-Pieters-Leeuw</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                    <div className="w-12 h-12 bg-[#B87333]/20 rounded-full flex items-center justify-center">
                      <Clock size={24} className="text-[#B87333]" />
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Horaires</p>
                      <p className="font-semibold">Lun - Ven : 8h - 18h</p>
                      <p className="text-white/80">Sam : 9h - 14h</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Zone d'intervention */}
              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h3 className="text-xl font-['Playfair_Display'] font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                  <MapPin className="text-[#B87333]" size={24} />
                  Zone d'intervention
                </h3>
                <p className="text-[#6B6B6B] mb-4">
                  Nous intervenons dans toute la région bruxelloise et ses environs :
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Bruxelles", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Woluwe", "Forest", "Anderlecht", "Schaerbeek"].map((zone) => (
                    <span 
                      key={zone}
                      className="bg-[#B87333]/10 text-[#B87333] px-3 py-1 rounded-full text-sm font-medium"
                    >
                      {zone}
                    </span>
                  ))}
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-[#25D366] rounded-3xl p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <MessageCircle size={40} />
                  <div>
                    <h3 className="text-xl font-bold">Préférez WhatsApp ?</h3>
                    <p className="opacity-90">Réponse rapide garantie</p>
                  </div>
                </div>
                <a
                  href={`https://wa.me/32488467522?text=${encodeURIComponent("Bonjour Perfect Team Renov 👋\n\nJe souhaite discuter d'un projet de rénovation à Bruxelles.\n\nMerci !")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-contact-btn"
                >
                  <Button className="w-full bg-white text-[#25D366] hover:bg-white/90 font-bold py-6 text-lg">
                    Démarrer une conversation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-center">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-green-500" size={20} />
              <span className="text-[#6B6B6B]">Devis gratuit sous 24-48h</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-green-500" size={20} />
              <span className="text-[#6B6B6B]">Sans engagement</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-green-500" size={20} />
              <span className="text-[#6B6B6B]">Visite gratuite sur place</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="text-green-500" size={20} />
              <span className="text-[#6B6B6B]">Garantie décennale</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
